// laneColumnWidths.js
//
// Field-type default column widths for Board Lanes (ClickUp-style).
// Persisted view.columnWidths override these.

import { normalizeSchemaFieldType } from 'utilities/FieldTypes';

export const LANE_TITLE_COLUMN_KEY = 'title';

const DEFAULT_BY_FIELD_TYPE = Object.freeze({
  title: 260,
  string: 180,
  text: 200,
  subtitle: 200,
  richtext: 220,
  enum: 140,
  pipeline: 140,
  multiselect: 160,
  tags: 140,
  people: 110,
  relation: 160,
  boolean: 88,
  number: 110,
  sequence: 110,
  percentage: 100,
  money: 120,
  currency: 100,
  date: 120,
  datetime: 150,
  annualdate: 120,
  daterange: 170,
  datetimerange: 190,
  phone: 140,
  email: 180,
  url: 180,
  location: 200,
  measurement: 130,
  barcode: 140,
  color: 88,
  checklist: 140,
  signature: 140,
});

const FALLBACK_WIDTH = 160;
const MIN_TITLE_WIDTH = 180;
const MIN_FIELD_WIDTH = 72;

/**
 * Default width for a Lanes column from field type (or title key).
 *
 * @param {string} columnKey
 * @param {object|null|undefined} fieldDef
 * @returns {number}
 */
export function getLaneDefaultColumnWidth(columnKey, fieldDef = null) {
  const key = String(columnKey || '').trim();
  if (key === LANE_TITLE_COLUMN_KEY) {
    return DEFAULT_BY_FIELD_TYPE.title;
  }
  if (key === 'dateCreated' || key === 'dateModified') {
    return DEFAULT_BY_FIELD_TYPE.date;
  }
  const fieldType = normalizeSchemaFieldType(fieldDef?.type || '');
  return DEFAULT_BY_FIELD_TYPE[fieldType] || FALLBACK_WIDTH;
}

/**
 * Minimum resize width for a Lanes column.
 *
 * @param {string} columnKey
 * @returns {number}
 */
export function getLaneMinColumnWidth(columnKey) {
  return String(columnKey || '').trim() === LANE_TITLE_COLUMN_KEY
    ? MIN_TITLE_WIDTH
    : MIN_FIELD_WIDTH;
}

/**
 * Resolves effective width: persisted override, else field-type default.
 *
 * @param {string} columnKey
 * @param {object|null|undefined} fieldDef
 * @param {Record<string, number>|null|undefined} effectiveColumnWidths
 * @returns {number}
 */
export function resolveLaneColumnWidth(columnKey, fieldDef, effectiveColumnWidths) {
  const override = effectiveColumnWidths?.[columnKey];
  if (typeof override === 'number' && override > 0) {
    return Math.max(getLaneMinColumnWidth(columnKey), Math.round(override));
  }
  return getLaneDefaultColumnWidth(columnKey, fieldDef);
}
